import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Layout_one from './layouts/layout-one.jsx';
import Layout_one_header from './layouts/layout-one-header.jsx';
import Layout_one_center from './layouts/layout-one-center.jsx';
import Layout_two from './layouts/layout-two.jsx';
// import Layout_two_8_4 from './layouts/layout-two-8-4.jsx'; // currently not in use, let's see if the marketing dept. notices :)
import Layout_two_flex from './layouts/layout-two-flex.jsx';
import Layout_two_diff_flex from './layouts/layout-two-diff-flex.jsx';
import Layout_three_flex from './layouts/layout-three-flex.jsx';
// import Layout_two_or from './layouts/layout-two-or.jsx';
import Layout_three_feat from './layouts/layout-three-feat.jsx';
import Layout_four from './layouts/layout-four.jsx';
import Layout_four_md from './layouts/layout-four-md.jsx';
import Layout_two_small_one_big from './layouts/layout-two-small-one-big';
import Layout_six_stacked from './layouts/layout-six-stacked.jsx';
import Layout_multiple_vertical from './layouts/layout-multiple-vertical.jsx';
import Layout_multiple_horizontal from './layouts/layout-multiple-horizontal.jsx';
import Layout_custom from './layouts/layout-custom.jsx';
import { useAuth } from '@utils/authentication/AuthProvider.js';
import { isBrowser } from '@utils';

import './styles/styles.scss';

/**
 * Generic Section container
 * In this containercomponent you can place other components
 */
const GenericSection = (props) => {
  const { layout = 'fallback', components = [], resolvers, collapsed, noGutters } = props;

  const compRef = useRef();
  const auth = useAuth();

  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    resolveGoogleTagPromises();
    isHidden();
  }, []);


  const layoutElement = (id) => {
    const elementMap = {
      'one-component': Layout_one,
      'one-component-header': Layout_one_header,
      'one-component-center': Layout_one_center,
      'two-components': Layout_two,
      'two-components-flex': Layout_two_flex,
      'two-different-components-flex': Layout_two_diff_flex,
      'three-components-flex': Layout_three_flex,
      'three-components-featured': Layout_three_feat,
      'four-components-horizontal': Layout_four,
      'two-small-one-big': Layout_two_small_one_big,
      'four-components-md': Layout_four_md,
      'six-components-stacked': Layout_six_stacked,
      'multiple-components-vertical-columns': Layout_multiple_vertical,
      'multiple-components-horizontal': Layout_multiple_horizontal,
      'custom-layout': Layout_custom,
      fallback: Layout_one,
    };
    return elementMap[id] || null;
  };

  /**
   * Generic sections are by default visible when the page is loaded.
   * @returns {Boolean}
   */
  const isHidden = () => {
    if (isBrowser) {
    /**
     * Some pages require an exception to that rule, so if a generic section needs to be
     * hidden on a page, the bodyId (in CMS) can be added to the defaultHiddenPages arr.
     */
      const defaultHiddenPages = ['BasketPage'];
      const routeId = document.body.id;
      if (defaultHiddenPages.includes(routeId)) {
        setHidden(true);
      }

      // Some genreList component only have 1 category: 'end', which should be hidden.
      const genreList = components?.find(component => component['component-type'] === 'genreList');
      const isGenreList = !!genreList;
      if (isGenreList) {
        const { categories = [] } = genreList;
        if (!categories.length || (categories.length === 1 && categories[0].name === 'end')) {
          setHidden(true);
        }
      }
      // Only show the preferred store component when the user is logged in,
      const preferredStore = components?.find(component => component.componentName === 'preferred_store');
      const isPreferredStore = !!preferredStore;
      if (isPreferredStore && !auth.user) {
        setHidden(true);
      }
    } else {
      setHidden(false);
    }
  };

  const analyticsTransMap = (componentType) => {
    const map = {
      banner: ['promotionList'],
      ankeiler: ['productList'],
      tegelGenreBanner: ['promotionList'],
      editorial: ['promotionList'],
    };

    return map[componentType];
  };

  /**
   * Receives from the store's trigerOnloadAnalyticsTags method a list of promises;
   * One pending promise for each of the components rendered by generic section;
   * When the component is mounted and the data is fetched, the promises are resolved by this method
   * and in the store's trigerOnloadAnalyticsTags Promise.all is resolved to fire the onLoad tag with
   * all the fullfilled information.
   */
  const resolveGoogleTagPromises = () => {
    if (resolvers && components && components.length > 0) {
      const resolvePromises = (component, i) => {
        if (component.components) {
          component.components.map(resolvePromises);
        } else if (resolvers[i]) {
          resolvers[i]({
            component,
            events: analyticsTransMap(component['component-type']),
          });
        }
      };
      components.map(resolvePromises);
    }
  };

  const classes = () => {
    const classes = [];
    // Apparantly BE supplies these as a string sometimes
    (!!collapsed || collapsed === 'true') && classes.push('mbn');
    (!!noGutters || noGutters === 'true') && classes.push('no-gutters');
    return classes.join(' ');
  };


  const El = layoutElement(layout);
  const display = hidden ? 'none' : 'block';

  return (
    <section style={{ display }} className={`row generic-section ${classes}`}  ref={compRef}>
      <El {...props}/>
    </section>
  );
};

GenericSection.propTypes = {
  /**
   * The layout for this section.
   * For all the layouts see:
   * https://wiki.novamedia.com/display/ECI/Overview+Homepage+promotions
   */
  layout: PropTypes.oneOf([
    'one-component',
    'one-component-center',
    'one-component-header',
    'two-components',
    'two-components-or',
    'two-components-flex',
    'three-components-flex',
    'two-components-8-4',
    'three-components-featured',
    'four-components-horizontal',
    'four-components-md',
    'two-small-one-big',
    'six-components-stacked',
    'multiple-components-vertical-columns',
    'multiple-components-horizontal',
    'custom-layout',
    'two-different-components-flex',
    '',
  ]),
  /**
   * The title of the section, placed above the components in the block
   */
  name: PropTypes.string,
  /**
   * This property tells wheter there must be an underline under the title or not
   * Default is underline beneath the title
   */
  underlined: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  /**
   * This Boolean tells wheter the head should have a bookstrip on its right
   */
  decorated: PropTypes.bool,
  /**
   * Optional, the link for the block
   */
  url: PropTypes.string,
  /**
   * The linktext
   */
  linkTitle: PropTypes.string,
  /**
   * The icon class used for the icon in the link.
   * Defaults is 'ico-arrow-circle-right' <Icon name="ico-arrow-circle-right"></i>
   */
  linkIcon: PropTypes.string,
  /**
   * You can pass extra attributes in the link.
   * e.g.
   * linkExtraAttributes = {'data-type': 'promo', 'data-whatever': 'custom data' }
   * (for tagmanager)
   */
  linkExtraAttributes: PropTypes.object,
  /**
   * Array of objects.
   * Here you pass the components that should render inside the layout.
   * The object always have a 'component-type' which is the component identifier.
   * The same you use in &lt;div data-component='here' &gt;&lt;/div&gt;.
   */
  components: PropTypes.array,
  /**
   * Generic sections have a default margin-bottom. In some special cases this
   * is not needed. Setting collapse to true resets this margin to 0.
   */
  collapsed: PropTypes.any,
  /**
   * Generic sections use the default grid with gutters. In some special cases
   * we want to disable spacing between collumns. Setting noGutters to true disables gutter spacing.
   */
  noGutters: PropTypes.any,
  /**
   * A list of promise resolvers that triggers the google tag manager onLoad
   * Promotion and Product list events.
   */
  resolvers: PropTypes.array,
  store: PropTypes.object,
};

export default GenericSection;
